import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { HexColorPicker } from 'react-colorful';

const LABEL = 'text-[10px] font-semibold text-slate-500 uppercase tracking-wide';
const DIALOG_Z = 70; // above app Modal (z-60)

const SWATCH_GRID_STYLE = {
  gridTemplateColumns: 'repeat(10, minmax(0, 1fr))',
  maxWidth: 256,
};

const swatchBtnClass = (sel) =>
  [
    'aspect-square min-h-0 w-full min-w-0 max-w-full border p-0 outline-none transition',
    'border-slate-300/90 hover:scale-[1.04] focus-visible:ring-2 focus-visible:ring-slate-500',
    sel ? 'z-[1] ring-2 ring-slate-900 ring-offset-1' : 'hover:z-[1]',
  ].join(' ');

function hexToRgb(h) {
  if (!h || !/^#[0-9A-Fa-f]{6}$/.test(h)) return { r: 0, g: 0, b: 0 };
  const n = parseInt(h.slice(1), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHex(r, g, b) {
  const a = (n) => Math.max(0, Math.min(255, Math.round(Number(n) || 0)));
  return `#${[a(r), a(g), a(b)].map((x) => x.toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

const THEME_FAMILIES_10x6 = [
  ['#FFFFFF', '#E7E6E6', '#C6C6C6', '#9A9A9A', '#666666', '#1E1E1E'],
  ['#F0F0F0', '#D0D0D0', '#A5A4A0', '#7A7974', '#4A4A4A', '#1D1C1A'],
  ['#E0ECF6', '#B0D0EA', '#5B9BD5', '#2E6FA8', '#1A4D78', '#0D2634'],
  ['#F4E3D7', '#E2B8A0', '#ED7D31', '#C45D1A', '#8A4210', '#4A2008'],
  ['#FDF0D0', '#F0E0A0', '#FFC000', '#D0A200', '#806000', '#403000'],
  ['#E0F0D5', '#B0E090', '#70AD47', '#4A9020', '#2A6010', '#153008'],
  ['#E0E8F5', '#A8B8D8', '#4472C4', '#2C5094', '#1A3460', '#0E1A32'],
  ['#E0E0E5', '#C0BCC8', '#A5A0B0', '#6A6078', '#3A3448', '#1C1A20'],
  ['#F0E0E4', '#E0A0B0', '#E74C7C', '#A01545', '#601028', '#300814'],
  ['#D8F0E0', '#A0E0C0', '#00B0A0', '#008878', '#005850', '#002C28'],
];

const THEME_6X10 = [0, 1, 2, 3, 4, 5].map((row) => THEME_FAMILIES_10x6.map((fam) => fam[row]));

const NO_COLOR_SWATCH_STYLE = {
  backgroundImage: 'repeating-linear-gradient(45deg, #cbd5e1 0, #cbd5e1 2px, #f1f5f9 2px, #f1f5f9 4px)',
};

const STANDARD_ROW = [
  '#A6A6A6',
  '#1F1F1F',
  '#E6E0EC',
  '#4F5D75',
  '#B7472A',
  '#FFC000',
  '#5B9BD5',
  '#70AD47',
  '#7F3F9F',
  '#003F7E',
];

function parseSelectedHex(v) {
  const s = String(v ?? '')
    .trim()
    .toUpperCase();
  return /^#[0-9A-F]{6}$/.test(s) ? s : null;
}

function WordStyleColorPicker({ value, onChange, onClearError, id: idProp }) {
  const selectedHex = useMemo(() => parseSelectedHex(value), [value]);
  const workingHex = selectedHex || '#E5E7EB';
  const [moreOpen, setMoreOpen] = useState(false);
  const [draftHex, setDraftHex] = useState(workingHex);
  const [rStr, setRStr] = useState('0');
  const [gStr, setGStr] = useState('0');
  const [bStr, setBStr] = useState('0');
  const [hexInput, setHexInput] = useState(workingHex);
  const uid = useId();
  const dialogId = `more-colors-${idProp || uid}`;

  const applyNoColor = () => {
    onChange('');
    onClearError?.();
  };

  const applyHex = (hex) => {
    if (hex === '' || hex == null) {
      applyNoColor();
      return;
    }
    onChange(typeof hex === 'string' && hex[0] === '#' ? hex.toUpperCase() : String(hex).toUpperCase());
    onClearError?.();
  };

  const syncFromDraft = useCallback((hex) => {
    const h = /^#[0-9A-Fa-f]{6}$/i.test(String(hex)) ? String(hex).toUpperCase() : '#E5E7EB';
    const { r, g, b } = hexToRgb(h);
    setRStr(String(r));
    setGStr(String(g));
    setBStr(String(b));
    setHexInput(h);
  }, []);

  useEffect(() => {
    if (!moreOpen) return;
    syncFromDraft(draftHex);
  }, [moreOpen, draftHex, syncFromDraft]);

  const commitDraftRgb = () => {
    const { r, g, b } = hexToRgb(draftHex);
    const rN = parseInt(rStr, 10);
    const gN = parseInt(gStr, 10);
    const bN = parseInt(bStr, 10);
    const rr = Number.isNaN(rN) ? r : Math.max(0, Math.min(255, rN));
    const gg = Number.isNaN(gN) ? g : Math.max(0, Math.min(255, gN));
    const bb = Number.isNaN(bN) ? b : Math.max(0, Math.min(255, bN));
    setRStr(String(rr));
    setGStr(String(gg));
    setBStr(String(bb));
    setDraftHex(rgbToHex(rr, gg, bb));
  };

  const commitHexInput = () => {
    const raw = hexInput.trim();
    if (/^#[0-9A-Fa-f]{6}$/i.test(raw)) {
      setDraftHex(raw.toUpperCase());
      onClearError?.();
      return;
    }
    const n = raw.replace(/^#/, '');
    if (/^[0-9A-Fa-f]{6}$/i.test(n)) {
      setDraftHex(`#${n.toUpperCase()}`);
    } else {
      setHexInput(draftHex);
    }
  };

  const openMore = () => {
    setDraftHex(workingHex);
    syncFromDraft(workingHex);
    setMoreOpen(true);
  };

  const confirmMore = () => {
    const h = /^#[0-9A-Fa-f]{6}$/i.test(String(draftHex)) ? String(draftHex).toUpperCase() : workingHex;
    applyHex(h);
    setMoreOpen(false);
  };

  const applyNoColorFromDialog = () => {
    applyNoColor();
    setMoreOpen(false);
  };

  const cancelMore = () => {
    setMoreOpen(false);
  };

  useEffect(() => {
    if (!moreOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        cancelMore();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [moreOpen]);

  const moreDialog =
    moreOpen &&
    createPortal(
      <div
        className="fixed inset-0 flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-[1px]"
        style={{ zIndex: DIALOG_Z, fontFamily: 'system-ui, "Segoe UI", sans-serif' }}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) cancelMore();
        }}
      >
        <div
          className="w-full max-w-[19rem] overflow-hidden rounded border border-slate-300/90 bg-slate-50 shadow-2xl ring-1 ring-black/10"
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby={dialogId}
        >
          <div className="border-b border-slate-200 bg-white px-3 py-2">
            <h3 id={dialogId} className="text-[13px] font-semibold text-slate-800">
              Custom color
            </h3>
          </div>
          <div className="bg-white p-3">
            <div className="mb-3 flex items-end justify-center gap-3" aria-hidden="true">
              <div className="text-center">
                <p className="mb-0.5 text-[9px] font-medium text-slate-500">Current</p>
                <div
                  className="h-8 w-10 border border-slate-300"
                  style={
                    selectedHex
                      ? { backgroundColor: selectedHex }
                      : NO_COLOR_SWATCH_STYLE
                  }
                />
              </div>
              <span className="mb-1 text-slate-400" aria-hidden="true">
                →
              </span>
              <div className="text-center">
                <p className="mb-0.5 text-[9px] font-medium text-slate-500">New</p>
                <div
                  className="h-8 w-10 border-2 border-slate-400"
                  style={{ backgroundColor: /^#[0-9A-Fa-f]{6}$/.test(draftHex) ? draftHex : '#888' }}
                />
              </div>
            </div>
            <div
              className="[&_.react-colorful]:!w-full [&_.react-colorful__saturation]:!h-36 [&_.react-colorful__saturation]:rounded-sm [&_.react-colorful__hue]:!h-2.5"
              style={{ minHeight: 0 }}
            >
              <HexColorPicker
                color={/^#[0-9A-Fa-f]{6}$/i.test(String(draftHex)) ? String(draftHex).toUpperCase() : workingHex}
                onChange={(c) => setDraftHex(String(c).toUpperCase())}
              />
            </div>
            <div className="mt-3 space-y-2">
              <label className="block">
                <span className="mb-0.5 block text-[10px] text-slate-500">Hex</span>
                <input
                  type="text"
                  className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-xs font-mono"
                  value={hexInput}
                  onChange={(e) => {
                    setHexInput(e.target.value);
                  }}
                  onBlur={commitHexInput}
                />
              </label>
              <div className="grid grid-cols-3 gap-1.5">
                <div>
                  <span className="mb-0.5 block text-[9px] text-slate-500">Red</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="w-full border border-slate-300 bg-white px-1.5 py-1 text-xs font-mono tabular-nums"
                    value={rStr}
                    onChange={(e) => setRStr(e.target.value.replace(/[^\d]/g, ''))}
                    onBlur={commitDraftRgb}
                  />
                </div>
                <div>
                  <span className="mb-0.5 block text-[9px] text-slate-500">Green</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="w-full border border-slate-300 bg-white px-1.5 py-1 text-xs font-mono tabular-nums"
                    value={gStr}
                    onChange={(e) => setGStr(e.target.value.replace(/[^\d]/g, ''))}
                    onBlur={commitDraftRgb}
                  />
                </div>
                <div>
                  <span className="mb-0.5 block text-[9px] text-slate-500">Blue</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="w-full border border-slate-300 bg-white px-1.5 py-1 text-xs font-mono tabular-nums"
                    value={bStr}
                    onChange={(e) => setBStr(e.target.value.replace(/[^\d]/g, ''))}
                    onBlur={commitDraftRgb}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-1.5 border-t border-slate-200 bg-slate-100/90 px-2.5 py-1.5">
            <button
              type="button"
              onClick={applyNoColorFromDialog}
              className="me-auto min-w-0 border border-slate-300 border-dashed bg-white px-2 py-1.5 text-xs font-medium text-slate-600 shadow-sm hover:bg-slate-50"
            >
              No color
            </button>
            <button
              type="button"
              onClick={cancelMore}
              className="min-w-[4.5rem] border border-slate-300 bg-white px-2 py-1.5 text-xs font-medium text-slate-800 shadow-sm hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmMore}
              className="min-w-[4.5rem] border border-sky-700 bg-sky-800 px-2 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-sky-700"
            >
              OK
            </button>
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <div
      className="w-full max-w-64 rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
      id={idProp}
      style={{ fontFamily: 'system-ui, "Segoe UI", sans-serif' }}
    >
      <button
        type="button"
        onClick={applyNoColor}
        aria-pressed={!selectedHex}
        aria-label="No color"
        className={[
          'mb-3 flex w-full items-center gap-3 rounded-xl border-2 bg-white px-3 py-2.5 text-left transition',
          !selectedHex
            ? 'border-slate-800 shadow-sm ring-2 ring-slate-900/10'
            : 'border-dashed border-slate-300 hover:border-slate-400 hover:bg-slate-50/90',
        ].join(' ')}
      >
        <span
          className="relative h-9 w-10 shrink-0 overflow-hidden rounded-md border border-slate-300/90 shadow-inner"
          style={NO_COLOR_SWATCH_STYLE}
          aria-hidden
        />
        <span className="min-w-0 flex-1 text-[13px] font-semibold text-slate-800">No color</span>
        {!selectedHex ? (
          <span
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-900 text-white"
            aria-hidden
          >
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </span>
        ) : null}
      </button>

      <p className={`mb-1.5 ${LABEL}`}>Theme colors</p>
      <div
        className="mb-2 grid w-full gap-px"
        style={SWATCH_GRID_STYLE}
        role="listbox"
        aria-label="Theme colors"
      >
        {THEME_6X10.map((row, ri) =>
          row.map((hex, ci) => {
            const sel = selectedHex === hex;
            return (
              <button
                key={`theme-${ri}-${ci}`}
                type="button"
                title={hex}
                aria-label={hex}
                aria-pressed={sel}
                onClick={() => applyHex(hex)}
                className={swatchBtnClass(sel)}
                style={{ backgroundColor: hex }}
              />
            );
          })
        )}
      </div>

      <p className={`mb-1.5 ${LABEL}`}>Standard colors</p>
      <div
        className="mb-2 grid w-full gap-px"
        style={SWATCH_GRID_STYLE}
        role="listbox"
        aria-label="Standard colors"
      >
        {STANDARD_ROW.map((hex) => {
          const sel = selectedHex === hex;
          return (
            <button
              key={hex}
              type="button"
              title={hex}
              aria-label={hex}
              aria-pressed={sel}
              onClick={() => applyHex(hex)}
              className={swatchBtnClass(sel)}
              style={{ backgroundColor: hex }}
            />
          );
        })}
      </div>

      <button
        type="button"
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 bg-gradient-to-b from-white to-slate-50 px-3 py-2.5 text-center text-xs font-semibold text-slate-800 shadow-sm transition hover:border-slate-400 hover:from-slate-50 hover:to-slate-100 hover:shadow"
        onClick={openMore}
        aria-haspopup="dialog"
        aria-controls={dialogId}
        aria-expanded={moreOpen}
      >
        <svg className="h-4 w-4 shrink-0 text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
          <circle cx="8" cy="9" r="3.25" strokeWidth="2" />
          <circle cx="16" cy="9" r="3.25" strokeWidth="2" />
          <circle cx="12" cy="15.5" r="3.25" strokeWidth="2" />
        </svg>
        <span className="leading-tight">More colors</span>
        <svg className="h-3.5 w-3.5 shrink-0 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {moreDialog}
    </div>
  );
}

export default WordStyleColorPicker;
